<template>
    <div class="dk-wizard-lead-form-modal-tabs">
        <div
            class="dk-wizard-lead-form-modal-tabs__header tw-relative tw-z-10 tw-w-full tw-shadow-[inset_0_-2px_0] tw-shadow-slate-100 lg:!tw-shadow-lg-down"
        >
            <div
                class="dk-wizard-lead-form-modal-tabs__tab-wrapper tw-relative tw-flex tw-items-stretch tw-justify-between lg:tw-mx-auto tw-overflow-x-auto tw-overflow-y-hidden"
            >
                <button
                    v-for="(tab, i) in tabs"
                    v-show="tab.isVisible"
                    :key="`tab-${_uid}-${i}`"
                    class="dk-wizard-lead-form-modal-tabs__tab tw-relative tw-z-10 tw-block tw-flex-none lg:tw-px-4 tw-py-2 tw-text-sm tw-leading-sm tw-border-b-2 tw-border-solid max-lg:tw-border-slate-100 hover:lg:tw-text-brand-primary tw-text-center lg:tw-border-b-transparent"
                    :class="tabClasses(tab)"
                    :aria-controls="tab.hash"
                    :aria-selected="tab.isActive"
                    role="tab"
                    @click="e => selectTab(tab.hash, e)"
                    v-html="tab.header"
                ></button>
            </div>
        </div>
        <div :class="slotClasses">
            <slot></slot>
        </div>
    </div>
</template>

<script>
/**
 * A component for displaying a tab system.
 *
 * @version 4.0.0
 * @example ./examples/DkTabs.md
 */
export default {
    name: 'DkWizardLeadFormTabs',
    data() {
        return {
            tabs: [],
            activeTabHash: '',
            lastActiveTabHash: '',
            selectedTabIndex: 0,
        };
    },
    computed: {
        opts() {
            return {
                spaceBetween: 20,
                lines: true,
            };
        },
        slotClasses() {
            return '';
        },
    },
    watch: {
        // Moved the call for `handleTabSelection()`
        // from `mounted()` to this watcher
        // because in some cases `mounted`
        // was triggered before the `v-for`
        // for the tabs, therefore tabs === []
        // when `handleTabSelection()` was called
        // and the first tab was not selected.
        tabs(tabsUpdated) {
            if (tabsUpdated && tabsUpdated.length) {
                this.handleTabSelection();
            }
        },
    },
    created() {
        this.tabs = this.$children;
    },
    methods: {
        selectTab(selectedTabHash, event) {
            const selectedTab = this.findTab(selectedTabHash);

            if (!selectedTab || selectedTab.disabled) {
                return;
            }

            if (this.lastActiveTabHash === selectedTab.hash) {
                /**
                 * This event is fired when the same tab has been clicked multiple times
                 * @event re-clicked
                 * @type {Object}
                 */
                return event && this.$emit('re-clicked', this.eventPayload(selectedTab, event));
            }

            for (const tab of this.tabs) {
                tab.isActive = tab.hash === selectedTab.hash;
            }

            this.selectedTabIndex = this.getTabIndex(selectedTabHash);
            this.lastActiveTabHash = this.activeTabHash = selectedTab.hash;

            /**
             * This event is fired when a tab has been clicked
             * @event change
             * @type {Object}
             */
            event && this.$emit('change', this.eventPayload(selectedTab, event));
        },
        handleTabSelection() {
            if (this.findTab(window.location.hash)) {
                return this.selectTab(window.location.hash);
            }

            if (this.tabs.length) {
                this.selectTab(this.tabs[0].hash);
            }
        },
        eventPayload(tab, event) {
            return {
                index: this.selectedTabIndex,
                tab,
                event,
            };
        },
        isLastTab(index) {
            return !(index < this.tabs.length - 1);
        },
        findTab(hash) {
            return this.tabs.find((tab) => tab.hash === hash);
        },
        getTabIndex(hash) {
            return this.tabs.indexOf(this.findTab(hash));
        },
        tabClasses(tab) {
            const classes = {
                '!tw-border-brand-primary': tab.isActive,
                'tw-text-base-subtle': !tab.isActive,
                'tw-pointer-events-none tw-cursor-default tw-text-disabled': tab.disabled,
            };

            switch (this.tabs.length) {
                case 2:
                    classes['tw-w-1/2'] = true;
                    break;
                case 3:
                    classes['tw-w-1/3'] = true;
                    break;
                case 4:
                    classes['tw-w-auto tw-min-w-[120px] lg:tw-w-1/4 tw-px-4 tw-whitespace-nowrap'] = true;
                    break;
                default:
                    classes['tw-w-auto tw-px-4 tw-whitespace-nowrap'] = true;
            }

            return classes;
        },
    },
};
</script>
